/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// Focal closes mega-menus on mouseleave of the <li>. That <li> is only the
// label; the panel is position:absolute against the sticky header. Moving
// onto the panel (or through the header padding toward the images) leaves
// the label, hits the page underneath (homepage slideshow after it paints),
// and the menu closes. Close when the pointer leaves the header instead.
// Items without a dropdown never call openDropdown, so also close when the
// pointer moves onto a top-level nav item that has no mega-menu.
(function () {
  const Ctor = customElements.get('desktop-navigation');
  if (!Ctor) return;

  const proto = Ctor.prototype;
  if (proto.__wsgHeaderHoverBound) return;
  proto.__wsgHeaderHoverBound = true;

  const origClose = proto.closeDropdown;
  const watchers = new WeakMap();
  const openingTimeouts = new WeakMap();

  function headerOf(nav) {
    return nav.closest('store-header') || nav.closest('.shopify-section--header');
  }

  function clearWatchers(parentElement) {
    const cleanup = watchers.get(parentElement);
    if (cleanup) cleanup();
  }

  function cancelPendingOpen(parentElement) {
    const pending = openingTimeouts.get(parentElement);
    if (pending == null) return;
    clearTimeout(pending);
    openingTimeouts.delete(parentElement);
  }

  function closeDropdowns(nav, exceptItem) {
    nav.querySelectorAll('.header__linklist-item.has-dropdown').forEach((item) => {
      if (item === exceptItem) return;
      cancelPendingOpen(item);
      const control = item.querySelector('[aria-controls]');
      if (control && control.getAttribute('aria-expanded') === 'true') {
        clearWatchers(item);
        origClose.call(nav, item);
      }
    });
  }

  function watchHeaderLeave(nav, parentElement) {
    clearWatchers(parentElement);
    const header = headerOf(nav);
    const menuItem = parentElement.querySelector('[aria-controls]');
    if (!header || !menuItem) return;

    const cleanup = () => {
      header.removeEventListener('mouseleave', onHeaderLeave);
      document.documentElement.removeEventListener('mouseleave', onDocumentLeave);
      watchers.delete(parentElement);
    };

    const closeIfOpen = () => {
      cleanup();
      if (menuItem.getAttribute('aria-expanded') === 'true') {
        origClose.call(nav, parentElement);
      }
    };

    const onHeaderLeave = (event) => {
      if (event.relatedTarget === null) return;
      closeIfOpen();
    };
    const onDocumentLeave = () => closeIfOpen();

    header.addEventListener('mouseleave', onHeaderLeave);
    document.documentElement.addEventListener('mouseleave', onDocumentLeave);
    watchers.set(parentElement, cleanup);
  }

  function bindNonDropdownHover(nav) {
    if (nav.__wsgNonDropdownBound) return;
    nav.__wsgNonDropdownBound = true;
    nav.addEventListener('mouseenter', (event) => {
      const node = event.target;
      if (!(node instanceof Element)) return;
      const item = node.closest('.header__linklist-item');
      if (!item || !nav.contains(item) || item.classList.contains('has-dropdown')) return;
      closeDropdowns(nav);
    }, true);
  }

  proto.openDropdown = function (parentElement) {
    closeDropdowns(this, parentElement);

    const menuItem = parentElement.querySelector('[aria-controls]');
    const dropdown = parentElement.querySelector(`#${menuItem.getAttribute('aria-controls')}`);
    this.currentMegaMenu = dropdown.classList.contains('mega-menu') ? dropdown : null;
    const header = headerOf(this);

    cancelPendingOpen(parentElement);
    const openingTimeout = setTimeout(() => {
      openingTimeouts.delete(parentElement);
      if (menuItem.getAttribute('aria-expanded') === 'true') {
        watchHeaderLeave(this, parentElement);
        return;
      }
      menuItem.setAttribute('aria-expanded', 'true');
      dropdown.removeAttribute('hidden');
      if (dropdown.classList.contains('mega-menu') && typeof MediaFeatures !== 'undefined' && !MediaFeatures.prefersReducedMotion()) {
        const items = Array.from(dropdown.querySelectorAll('.mega-menu__column, .mega-menu__image-push'));
        items.forEach((item) => {
          item.getAnimations().forEach((animation) => animation.cancel());
          item.style.opacity = 0;
        });
        const animation = new CustomAnimation(new ParallelEffect(items.map((item, index) => {
          return new CustomKeyframeEffect(item, {
            opacity: [0, 1],
            transform: ['translateY(20px)', 'translateY(0)']
          }, {
            duration: 250,
            delay: 100 + 60 * index,
            easing: 'cubic-bezier(0.65, 0, 0.35, 1)'
          });
        })));
        animation.play();
      }
      watchHeaderLeave(this, parentElement);
      this.dispatchEvent(new CustomEvent('desktop-nav:dropdown:open', { bubbles: true }));
    }, 100);
    openingTimeouts.set(parentElement, openingTimeout);

    parentElement.addEventListener('mouseleave', (event) => {
      if (!openingTimeouts.has(parentElement)) return;
      if (header && event.relatedTarget && header.contains(event.relatedTarget)) return;
      cancelPendingOpen(parentElement);
    }, { once: true });
  };

  document.querySelectorAll('desktop-navigation').forEach(bindNonDropdownHover);

  const origConnected = proto.connectedCallback;
  if (typeof origConnected === 'function') {
    proto.connectedCallback = function () {
      origConnected.call(this);
      bindNonDropdownHover(this);
    };
  }
})();

// On phones, large product galleries keep the existing Focal variant picker
// close to the media. The placeholder preserves the merchant-defined desktop
// block order when the viewport crosses the phone breakpoint.
(function () {
  const PHONE_QUERY = '(max-width: 740px)';
  const PLACEHOLDER_ATTRIBUTE = 'data-mobile-variant-picker-placeholder';

  function arrangeLargeGalleryVariantPickers() {
    const isPhone = window.matchMedia(PHONE_QUERY).matches;

    document.querySelectorAll('product-media[compact-mobile-pagination]').forEach((media) => {
      const product = media.closest('.product');
      const form = product && product.querySelector('.product__info .product-form');
      const picker = form && form.querySelector(':scope > variant-picker');
      if (!form || !picker) return;

      let placeholder = form.querySelector(`:scope > [${PLACEHOLDER_ATTRIBUTE}]`);
      if (!placeholder) {
        placeholder = document.createElement('span');
        placeholder.hidden = true;
        placeholder.setAttribute(PLACEHOLDER_ATTRIBUTE, '');
        picker.before(placeholder);
      }

      if (isPhone) {
        if (form.firstElementChild !== picker) form.prepend(picker);
      } else if (picker.previousElementSibling !== placeholder) {
        placeholder.after(picker);
      }
    });
  }

  document.addEventListener('DOMContentLoaded', arrangeLargeGalleryVariantPickers);
  window.addEventListener('load', arrangeLargeGalleryVariantPickers);
  document.addEventListener('product:rerender', () => queueMicrotask(arrangeLargeGalleryVariantPickers), true);
  const phoneMediaQuery = window.matchMedia(PHONE_QUERY);
  if (phoneMediaQuery.addEventListener) {
    phoneMediaQuery.addEventListener('change', arrangeLargeGalleryVariantPickers);
  } else {
    phoneMediaQuery.addListener(arrangeLargeGalleryVariantPickers);
  }
})();

// Trustpilot injects review widgets from content_for_header.
// 1) Hide the reviews carousel (template 53aa8912…) when it is not in a
//    rich_text section (stale xpath / duplicate).
// 2) Lower inline z-index: 1000 !important so widgets cannot sit above the
//    sticky header (z-index: 4). Restored from 657c94f after 52d595f dropped
//    it while adding the stray-carousel hide. Do not hide the header bar.
// 3) Preserve the Micro Combo node while Focal partially rerenders a variant.
//    Keep product-meta mounted and update its price list in place so the
//    Trustpilot iframe never disconnects or reloads.
(function () {
  const CAROUSEL_TEMPLATE_ID = '53aa8912dec7e10d38f59f36';
  const MICRO_COMBO_TEMPLATE_ID = '5419b6ffb0d04a076446a9af';
  const TRUSTPILOT_SELECTOR = '[id*="trustpilot-widget"], .trustpilot-widget';
  const TRUSTPILOT_WRAPPER_SELECTOR = '[id^="trustpilot-widget-trustbox"][id$="-wrapper"]';

  function findObservedProduct(form) {
    const formId = form && form.getAttribute('id');
    if (!formId) return null;

    return Array.from(document.querySelectorAll('product-rerender[allow-partial-rerender][observe-form]')).find((product) => {
      return product.getAttribute('observe-form') === formId;
    }) || null;
  }

  function findMicroCombo(product) {
    return product && product.querySelector(`.trustpilot-widget[data-template-id="${MICRO_COMBO_TEMPLATE_ID}"]`);
  }

  function removeDuplicateMicroCombos(product, keepWrapper) {
    if (!product) return;

    const wrappers = new Set();
    product.querySelectorAll(`.trustpilot-widget[data-template-id="${MICRO_COMBO_TEMPLATE_ID}"]`).forEach((widget) => {
      const wrapper = widget.closest(TRUSTPILOT_WRAPPER_SELECTOR);
      if (wrapper) wrappers.add(wrapper);
    });

    wrappers.forEach((wrapper) => {
      if (wrapper !== keepWrapper) wrapper.remove();
    });
  }

  function updatePriceList(productMeta, refreshedMeta, wrapper) {
    const currentPriceList = productMeta.querySelector('.price-list');
    const refreshedPriceList = refreshedMeta.querySelector('.price-list');

    if (currentPriceList && refreshedPriceList) {
      currentPriceList.replaceWith(refreshedPriceList);
    } else if (!currentPriceList && refreshedPriceList) {
      wrapper.parentNode.insertBefore(refreshedPriceList, wrapper);
    } else if (currentPriceList && !refreshedPriceList) {
      currentPriceList.remove();
    }
  }

  // This capture listener runs before Focal's product-rerender listener on the
  // product form. Temporarily changing the block type makes Focal skip only
  // product-meta while it still refreshes payment terms and every other native
  // partial block. The price list is taken from the same fetched Focal markup.
  document.addEventListener('product:rerender', (event) => {
    if (event.detail && event.detail.productChange) return;

    const product = findObservedProduct(event.target);
    const widget = findMicroCombo(product);
    const wrapper = widget && widget.closest(TRUSTPILOT_WRAPPER_SELECTOR);
    if (!product || !wrapper) return;

    const productMeta = wrapper.closest('[data-block-type="meta"]');
    const blockId = productMeta && productMeta.getAttribute('data-block-id');
    const refreshedMeta = blockId && event.detail && event.detail.htmlFragment && event.detail.htmlFragment.querySelector(
      `[data-block-type="meta"][data-block-id="${CSS.escape(blockId)}"]`
    );
    if (!productMeta || !refreshedMeta) return;

    productMeta.setAttribute('data-block-type', 'meta-preserved');
    updatePriceList(productMeta, refreshedMeta, wrapper);

    queueMicrotask(() => {
      productMeta.setAttribute('data-block-type', 'meta');
      removeDuplicateMicroCombos(product, wrapper);
      applyTrustpilotFixes();
    });
  }, true);

  function hideStray() {
    document.querySelectorAll('[id^="trustpilot-widget-trustbox"][id$="-wrapper"]').forEach((wrapper) => {
      const inner = wrapper.querySelector('.trustpilot-widget') || wrapper;
      const template = (inner.getAttribute && inner.getAttribute('data-template-id')) || '';
      const section = wrapper.closest('.shopify-section');
      if (!section) return;
      if (template === CAROUSEL_TEMPLATE_ID && (section.id || '').indexOf('rich_text') === -1) {
        wrapper.style.setProperty('display', 'none', 'important');
      }
    });
  }

  function resetTrustpilotZIndex() {
    document.querySelectorAll(TRUSTPILOT_SELECTOR).forEach((element) => {
      const zIndex = parseInt(element.style.zIndex, 10);
      if (!Number.isNaN(zIndex) && zIndex > 4) {
        element.style.setProperty('z-index', 'auto', 'important');
      }
    });
  }

  function applyTrustpilotFixes() {
    hideStray();
    resetTrustpilotZIndex();
  }

  document.addEventListener('DOMContentLoaded', applyTrustpilotFixes);
  window.addEventListener('load', applyTrustpilotFixes);
  new MutationObserver(applyTrustpilotFixes).observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style']
  });
})();
